import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';

const SubmitReview = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appointmentData, setAppointmentData] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const appointmentId = queryParams.get('appointmentId');
  const serviceName = queryParams.get('service');

  useEffect(() => {
    if (appointmentId) {
      loadAppointmentData();
    }
  }, [appointmentId]);

  const loadAppointmentData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const appointmentRef = doc(db, 'appointments', appointmentId);
      const appointmentSnap = await getDoc(appointmentRef);
      
      if (appointmentSnap.exists() && appointmentSnap.data().userId === currentUser.uid) {
        setAppointmentData(appointmentSnap.data());
      } else {
        console.error('Appointment not found or user not authorized');
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingClick = (value) => {
    setRating(value);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !appointmentId || rating === 0) {
      alert('Por favor, proporciona una calificación antes de enviar tu reseña.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Crear documento de reseña en Firestore
      await addDoc(collection(db, 'reviews'), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email,
        appointmentId,
        service: serviceName || appointmentData?.service,
        rating,
        reviewText,
        createdAt: new Date()
      });
      
      setSubmitted(true);
      setReviewText('');
      setRating(0);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Ha ocurrido un error al enviar tu reseña. Por favor, inténtalo de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar estrellas para la calificación
  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          style={{
            fontSize: '2rem',
            cursor: 'pointer',
            color: (hoverRating || rating) >= i ? '#FFD700' : '#E0E0E0'
          }}
          onClick={() => handleRatingClick(i)}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <Layout>
      <div style={{
        padding: '2rem',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <h1 style={{
          marginBottom: '1.5rem',
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#2e7d32'
        }}>
          {submitted ? '¡Gracias por tu reseña!' : 'Dejar una Reseña'}
        </h1>
        
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem'
          }}>
            <div className="modern-loading-spinner"></div>
          </div>
        ) : submitted ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: '#e8f5e9',
            borderRadius: '12px',
            border: '1px solid #4caf50'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ fontSize: '1.5rem', color: '#2e7d32', marginBottom: '1rem' }}>
              ¡Tu reseña ha sido enviada con éxito!
            </h2>
            <p style={{ marginBottom: '2rem' }}>
              Agradecemos que hayas compartido tu experiencia. 
              Tu retroalimentación es muy importante para nosotros.
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/appointments'}
              style={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                border: 'none',
                color: 'white',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Volver a Mis Citas
            </button>
          </div>
        ) : (
          <div className="modern-card" style={{
            padding: '2rem',
            borderRadius: '12px'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ 
                fontSize: '1.25rem', 
                marginBottom: '0.5rem',
                fontWeight: '600' 
              }}>
                Servicio: {serviceName || appointmentData?.service || 'Servicio'}
              </h2>
              {appointmentData && (
                <p>Fecha: {appointmentData.date} a las {appointmentData.time}</p>
              )}
            </div>
            
            <form onSubmit={handleSubmitReview}>
              <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '1.25rem', 
                  marginBottom: '1rem',
                  fontWeight: '600' 
                }}>
                  ¿Cómo calificarías nuestro servicio?
                </label>
                <div style={{ 
                  fontSize: '2rem', 
                  marginBottom: '0.5rem' 
                }}>
                  {renderStars()}
                </div>
                <p style={{ fontSize: '0.875rem', color: '#666' }}>
                  {rating === 0 ? 'Selecciona una calificación' : 
                   rating === 1 ? 'Muy insatisfecho' :
                   rating === 2 ? 'Insatisfecho' :
                   rating === 3 ? 'Regular' :
                   rating === 4 ? 'Satisfecho' : 'Muy satisfecho'}
                </p>
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '1.25rem', 
                  marginBottom: '1rem',
                  fontWeight: '600' 
                }}>
                  Comparte tu experiencia (opcional)
                </label>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Cuéntanos más sobre tu experiencia con nuestro servicio..."
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                ></textarea>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={loading || rating === 0}
                  style={{
                    background: rating === 0 ? '#9ca3af' : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    border: 'none',
                    color: 'white',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: rating === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Enviando...' : 'Enviar Reseña'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SubmitReview;
